import { apiClient } from './api';
import type {
  Gym,
  CreateGymInput,
  ApiCollection,
} from './types';

export interface GymEquipmentListResponse {
  equipment: any[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface GymExerciseListResponse {
  exercises: any[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface WorkoutGymSelectorResponse {
  gyms: Gym[];
  defaultGymId: number | null;
}

export interface SetDefaultGymResponse {
  defaultGymId: number | null;
}

export class GymService {
  private static readonly ENDPOINT = '/api/gyms';

  static async getAll(): Promise<Gym[]> {
    const response = await apiClient.get<ApiCollection<Gym>>(this.ENDPOINT);
    return response.data['member'] || [];
  }

  static async getById(id: number): Promise<Gym> {
    const response = await apiClient.get<Gym>(`${this.ENDPOINT}/${id}`);
    return response.data;
  }

  static async create(gym: CreateGymInput): Promise<Gym> {
    const response = await apiClient.post<Gym>(this.ENDPOINT, gym);
    return response.data;
  }

  static async update(id: number, gym: Partial<CreateGymInput>): Promise<Gym> {
    const response = await apiClient.patch<Gym>(`${this.ENDPOINT}/${id}`, gym);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.ENDPOINT}/${id}`);
  }

  static async getGymEquipment(gymId: number, limit = 50, offset = 0): Promise<GymEquipmentListResponse> {
    const response = await apiClient.get<GymEquipmentListResponse>(
      `/api/gyms/${gymId}/equipment?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  static async getGymExercises(gymId: number, limit = 50, offset = 0): Promise<GymExerciseListResponse> {
    const response = await apiClient.get<GymExerciseListResponse>(
      `/api/gyms/${gymId}/exercises?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  static async getWorkoutGymSelector(): Promise<WorkoutGymSelectorResponse> {
    const response = await apiClient.get<WorkoutGymSelectorResponse>('/api/workout/gym-selector');
    return response.data;
  }

  static async setDefaultGym(gymId: number): Promise<SetDefaultGymResponse> {
    const response = await apiClient.patch<SetDefaultGymResponse>('/api/users/default-gym', { gymId });
    return response.data;
  }
}

export default GymService;
